import {
  ArrowRight,
  ArrowRightCircle,
  ArrowsUpFromLineIcon,
  ArrowUpFromDot,
  ArrowUpFromLine,
  ArrowUpFromLineIcon,
  ArrowUpRight,
  ChartNoAxesCombined,
  CheckCircle,
  ChessQueenIcon,
  ChevronLeft,
  ChevronRight,
  Dot,
  SquareStackIcon,
  SuperscriptIcon,
  ThumbsUp,
  UserRoundCheck,
} from "lucide-react";
import React, { useRef } from "react";
import { motion } from "framer-motion";
import { slideLeft } from "../../animations/variants";

import { TrendingUp, ShieldCheck, Building2 } from "lucide-react";

import { useSelector } from "react-redux";
import {
  businessAdvisoryCTA,
  BusinessAdvisoryData,
} from "i18n/businessAdvisory";
import { Square2StackIcon } from "@heroicons/react/24/outline";
import {
  businessConsultingCTA,
  BusinessConsultingData,
} from "i18n/businessConsulting";
import { BusinessNetworksData } from "i18n/networks";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

const Listnetworks = () => {
  const lang = useSelector((state) => state.language.lang);
  const consults = BusinessNetworksData[lang];
  const icons = [
    SquareStackIcon,
    ChessQueenIcon,
    ArrowsUpFromLineIcon,
    UserRoundCheck,
  ];
  const cta = businessConsultingCTA[lang];

  const networkColors = {
    BDI: "#1F436B", // navy
    VBON: "#C82128", // red
    VBC: "#1F6A4F", // green
  };
  return (
    <section className="py-20 md:py-20  ">
      <div className="text-center my-14">
        <h2 className="text-4xl md:text-6xl font-[Crimson] text-reddark leading-tight">
          {lang === "vi" ? "Mạng lưới doanh nghiệp" : "Business Networks"}
        </h2>{" "}
        <div className="w-24 h-[2px] bg-reddark mx-auto mt-8 mb-8" />
        <p className="text-text text-base md:text-md mb-3 max-w-7xl mx-auto px-6 text-left">
          {consults.desc}
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        <Swiper
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          spaceBetween={18}
          loop
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
            },
            768: {
              slidesPerView: 2,
            },
            1200: {
              slidesPerView: 3,
            },
          }}
        >
          {consults.networks.map((service, index) => {
            const Icon = icons[index];

            return (
              <SwiperSlide key={service.id}>
                <ServiceCard service={service} Icon={Icon} lang={lang} />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, Icon, lang }) => (
  <motion.a
    key={service.id}
    href={service.link}
    target="_blank"
    rel="noopener noreferrer"
    variants={slideLeft}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
    className="
    group
    flex flex-col
    overflow-hidden
    rounded-3xl
    border border-border
    bg-white
    shadow-sm
    hover:shadow-[0_25px_60px_rgba(0,0,0,0.12)]
    hover:-translate-y-2
    transition-all duration-500 mx-6 lg:mx-0 mb-6
  "
  >
    <div className="relative h-64 overflow-hidden">
      <img
        src={service.image}
        alt={service.title}
        className="
        w-full h-full object-cover
        transition-transform duration-700
        group-hover:scale-110
      "
      />

      <div
        className="
        absolute inset-0
        bg-gradient-to-t
        from-black/80
        via-black/20
        to-transparent
      "
      />

      <div
        className="
        absolute top-5 left-5
        flex items-center gap-2
        px-4 py-2
        rounded-full
       backdrop-blur
        text-white
        text-xs font-semibold
        uppercase tracking-wider
      "
        style={{ background: service.color }}
      >
        <ThumbsUp size={14} />
        {service.code}
      </div>
    </div>

    {/* CONTENT */}
    <div className="flex flex-col flex-1 p-8">
      <div className="text-center px-0 py-6">
        <div
          className="
    w-14 h-14
    mx-auto
    rounded-2xl
    bg-gray-100  flex items-center justify-center
    mb-5
  "
        >
          <Icon className="w-7 h-7 k" style={{ color: service.color }} />
        </div>
        <h3
          className="
      text-2xl md:text-3xl
      font-[Crimson]
      font-semibold
      
      leading-tight
    "
          style={{ color: service.color }}
        >
          {service.title}
        </h3>

        <p className="italic text-[#9B6B53] text-sm mb-6">{service.subtitle}</p>

        <p className="text-gray-700 leading-relaxed">{service.description}</p>
      </div>

      <div className="border-t border-gray-200 px-0 py-3 ">
        <div className="grid grid-cols-3 text-center ">
          <div>
            <div className="font-bold text-lg" style={{ color: service.color }}>
              {service.members}
            </div>
            <div className="text-xs text-gray-500">{service.membersLabel}</div>
          </div>

          <div>
            <div className="font-bold text-lg" style={{ color: service.color }}>
              {service.events}
            </div>
            <div className="text-xs text-gray-500">{service.eventsLabel}</div>
          </div>

          <div>
            <div className="font-bold text-lg" style={{ color: service.color }}>
              {service.region}
            </div>
            <div className="text-xs text-gray-500">{service.regionLabel}</div>
          </div>
        </div>
      </div>
      {/* CTA */}
      <div className="pt-8">
        <div
          className="
          flex items-center justify-between
          border-t border-border 
          pt-6
        "
        >
          <span
            className="
            uppercase
            tracking-[0.25em]
            text-xs
            font-semibold
          "
            style={{ color: service.color }}
          >
            {lang === "vi" ? "Tham gia cộng đồng" : "Join Network"}
          </span>

          <div
            className="
            w-11 h-11
            rounded-full            text-white
            flex items-center justify-center
            transition-all duration-300
            group-hover:rotate-45
          "
            style={{ background: service.color }}
          >
            <ArrowUpRight size={18} />
          </div>
        </div>
      </div>
    </div>
  </motion.a>
);

export default Listnetworks;
